import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "@learningblocks/shared-components/lib/FormattedMessage";
import { Player } from "@lottiefiles/react-lottie-player";
import { useIntl } from "react-intl";

import className from "../style.module.scss";

export function AnimationEntity(props) {
  const intl = useIntl(); // current language = intl.locale
  const player = React.createRef();
  const [triggered, setTriggered] = useState(false);

  // Check if playOnce is defined; default to false if undefined
  const playOnce = props.data.playOnce ?? false;

  const baseUrl =
    window.CONTENT_BASE_URL ||
    window.location.origin + window.location.pathname;

  useEffect(() => {
    props.undoTrigger(props.data.customTrigger);
  }, []);

  useEffect(() => {
    if (!props.playedStates.has(props.state)) {
      initiatePlay(); // Only play if state hasn't been played before
    }
  }, [props.state, props.hidden]);

  const initiatePlay = () => {
    if (!props.hidden) {
      player.current?.stop();
      if (props.data.frameRange.start !== props.data.frameRange.end) {
        player.current.setPlayerSpeed(props.data.speed);
        player.current.setSeeker(props.data.frameRange.start, true);
        player.current.play();
      } else {
        player.current.setSeeker(props.data.frameRange.start, false);
      }
    }
  };

  const isLoaded = () => {
    if (!props.playedStates.has(props.state)) {
      initiatePlay();
    }
  };

  const newFrame = () => {
    if (
      Math.ceil(player.current.state.instance.currentFrame) ===
      props.data.frameRange.end
    ) {
      if (props.data.loop) {
        player.current.setSeeker(props.data.frameRange.start, true);
      } else {
        player.current.pause();
      }

      // Mark the state as played at the end frame only if playOnce is true
      if (playOnce && !props.playedStates.has(props.state)) {
        props.playedStates.add(props.state);
      }

      if (props.data.customTrigger !== "" && !triggered) {
        setTriggered(true);
        props.fireTrigger(props.data.customTrigger);
      }
    }
  };

  const handleEnd = () => {
    if (props.data.loop) {
      player.current.setSeeker(props.data.frameRange.start, true);
      player.current.play();
    }

    // Mark the state as played on complete only if playOnce is true
    if (playOnce && !props.playedStates.has(props.state)) {
      props.playedStates.add(props.state);
    }

    if (props.data.customTrigger !== "" && !triggered) {
      setTriggered(true);
      props.fireTrigger(props.data.customTrigger);
    }
  };

  const [src, setSrc] = useState(
    props.preview
      ? props.previewSrc
      : `${baseUrl}locales/${intl.locale}/animations/${props.src}`
  );

  const loadError = () => {
    props.preview
      ? props.previewSrc
      : setSrc(`${baseUrl}locales/en/animations/${props.src}`);
  };

  useEffect(() => {
    props.preview
      ? props.previewSrc
      : setSrc(`${baseUrl}locales/${intl.locale}/animations/${props.src}`);
  }, [intl.locale]);

  const ariaLive = props.data.ariaLive;
  const shouldAnnounce = ariaLive && ariaLive !== "none" && !props.hidden;

  return (
    <div
      className={className.entityContainer}
      style={{
        overflow: !props.allowOverflow ? "hidden" : null,
        display: props.hidden ? "none" : null,
      }}
      aria-hidden={props.hidden ? true : false}
      aria-label={
        props.data.altText !== ""
          ? FormattedMessage({
              string: props.data.altText,
            })
          : ""
      }
      {...(shouldAnnounce && { "aria-live": ariaLive })}
    >
      <div className={className.animationEntityContainer} aria-hidden>
        <Player
          key={src} // used to rerender when src changes
          ref={player}
          src={src}
          onEvent={(event) => {
            if (event === "error") loadError();
            if (event === "load") isLoaded();
            if (event === "frame") newFrame();
            if (event === "complete") handleEnd();
          }}
          keepLastFrame
          preserveAspectRatio="xMaxYMid meet"
          style={{
            width: "100%",
          }}
        ></Player>
      </div>
    </div>
  );
}

AnimationEntity.propTypes = {
  data: PropTypes.shape({
    frameRange: PropTypes.shape({
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
    }).isRequired,
    speed: PropTypes.number.isRequired,
    loop: PropTypes.bool.isRequired,
    altText: PropTypes.string.isRequired,
    playOnce: PropTypes.bool, // Optional playOnce property
    ariaLive: PropTypes.oneOf(["none", "polite", "assertive"]),
  }).isRequired,
  src: PropTypes.string.isRequired,
  allowOverflow: PropTypes.bool.isRequired,
  state: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
  playedStates: PropTypes.object.isRequired, // Passed from parent
  hidden: PropTypes.bool,
  preview: PropTypes.bool,
  previewSrc: PropTypes.string,
  undoTrigger: PropTypes.func,
  fireTrigger: PropTypes.func,
};
